import { FeedbackType } from '@enview/interface/types/actions/FeedbackData';
import { ReactElement, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as BillAnalytics from '../../../analytics/BillAnalytics';
import { BillAPI } from '../../../api';
import FeedbackPrompt from '../Prompts/FeedbackPrompt';
import { formatPlainTextToReact } from './utils';

type BillAutoSummaryProps = {
  billId: string;
  versionId: number;
};

/**
 * Demo component to fetch and display a generated bill summary
 * from the Data Insights API
 *
 * @param props
 * @constructor
 */
const BillAutoSummary = (props: BillAutoSummaryProps): ReactElement => {
  const { billId, versionId } = props;
  const {
    data: summaryResponse,
    isError,
    isLoading,
  } = BillAPI.endpoints.getBillAutoSummary.useQuery({
    billId,
    billVersionId: versionId,
  });
  const { t } = useTranslation();
  const [isSummaryAccurate, setIsSummaryAccurate] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    if (isSummaryAccurate !== undefined && summaryResponse?.summary) {
      BillAnalytics.trackBillAutoSummaryFeedback(
        billId,
        versionId,
        isSummaryAccurate,
        summaryResponse.summary,
      );
    }
  }, [isSummaryAccurate, summaryResponse?.summary]);

  const getText = (): ReactElement[] | ReactElement | string => {
    if (summaryResponse && summaryResponse.insightsApiError) {
      return t(summaryResponse.errorResourceCode);
    }
    if (summaryResponse && summaryResponse.summary) {
      return (
        <>
          <p className="ai-disclaimer-message">{t('bill.sidebar.aiDisclaimer')}</p>
          {formatPlainTextToReact(summaryResponse.summary)}
          <FeedbackPrompt
            feedbackType={FeedbackType.BillSummaryInaccurate}
            setFeedbackState={setIsSummaryAccurate}
            submitFeedbackMetadata={{
              billId: billId,
              billVersionId: versionId,
              summary: summaryResponse.summary,
            }}
          />
        </>
      );
    }
    if (isError) {
      return t('error.genericApiErrorMessage');
    }
    return <></>;
  };

  if (isLoading) {
    return (
      <div className="m-auto mt-3" style={{ width: '25px' }}>
        <Spinner animation="border" role="status" variant="secondary" />
      </div>
    );
  }

  return <div className="ai-generated-text">{getText()}</div>;
};

export default BillAutoSummary;
